/**
 * Community Service
 *
 * API calls for the community dashboard card endpoints.
 * Base URL is configured by VITE_API_BASE_URL (includes /api/v1).
 */

import { getApiClient } from './client';
import type {
  CommunityImpactResponse,
  UpcomingEventsResponse,
  CommunityUpdatesResponse,
} from '@/types/community.types';

/**
 * Fetch community impact metrics for a company.
 *
 * @param companyId - The company ID
 * @returns Promise with community impact data
 */
export async function getCommunityImpact(
  companyId: string | number
): Promise<CommunityImpactResponse> {
  try {
    const apiClient = getApiClient();
    const response = await apiClient.get<CommunityImpactResponse>(`/community/${companyId}/impact`);
    return response.data;
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(`Failed to fetch community impact: ${error.message}`);
    }
    throw error;
  }
}

/**
 * Fetch upcoming events for a company.
 *
 * @param companyId - The company ID
 * @returns Promise with upcoming events data
 */
export async function getUpcomingEvents(
  companyId: string | number
): Promise<UpcomingEventsResponse> {
  try {
    const apiClient = getApiClient();
    const response = await apiClient.get<UpcomingEventsResponse>(`/community/${companyId}/events`);
    return response.data;
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(`Failed to fetch upcoming events: ${error.message}`);
    }
    throw error;
  }
}

/**
 * Fetch community updates / news for a company.
 *
 * @param companyId - The company ID
 * @returns Promise with community updates data
 */
export async function getCommunityUpdates(
  companyId: string | number
): Promise<CommunityUpdatesResponse> {
  try {
    const apiClient = getApiClient();
    const response = await apiClient.get<CommunityUpdatesResponse>(
      `/community/${companyId}/updates`
    );
    return response.data;
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(`Failed to fetch community updates: ${error.message}`);
    }
    throw error;
  }
}
